import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button.js';
import { GameCard } from '../components/lobby/GameCard.js';
import { CasinoLiveBets } from '../components/lobby/CasinoLiveBets.js';
import { SeamlessGameFrame } from '../components/lobby/SeamlessGameFrame.js';
import { EmptyState } from '../components/lobby/EmptyState.js';
import { GamesGridSkeleton } from '../components/lobby/GamesGridSkeleton.js';
import { SiteFooter } from '../components/site/SiteFooter.js';
import { useAuthStore } from '../stores/auth.js';
import { launchGame, PopupBlockedError } from '../features/games/launchGame.js';
import { mapApiError } from '../lib/api/error.js';
import { fetchGames, GAMES_QUERY_KEYS, type Game, type LaunchMethod } from '../features/games/api.js';

export const Route = createFileRoute('/_player/sports')({
  component: SportsPage,
});

interface Embed {
  url: string;
  method: LaunchMethod;
  payload: Record<string, unknown> | null;
  title: string;
  /** Kept so the frame can explain a promotion that forbids this book (#546). */
  game: Game;
}

function SportsPage() {
  const { t, i18n } = useTranslation(['casino', 'common']);
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [launchingId, setLaunchingId] = useState<string | null>(null);
  const [embed, setEmbed] = useState<Embed | null>(null);

  // Filtered server-side: the catalog endpoint clamps a page to 200 games, so a
  // client-side filter over the newest page loses sportsbooks as the catalog grows.
  const gamesQuery = useQuery({
    queryKey: GAMES_QUERY_KEYS.list({ category: 'sport' }),
    queryFn: () => fetchGames({ category: 'sport', pageSize: 200 }),
    staleTime: 30_000,
  });

  const sportGames = gamesQuery.data?.items ?? [];

  async function onPlay(game: Game) {
    if (!accessToken) {
      void navigate({ to: '/login' });
      return;
    }
    if (!game.providerId) return;
    setLaunchingId(game.id);
    try {
      const outcome = await launchGame({
        providerId: game.providerId,
        gameId: game.id,
        lang: i18n.language ?? 'th',
        hint:
          game.launchMode && game.launchMethod
            ? { mode: game.launchMode, method: game.launchMethod }
            : null,
      });
      // Sportsbooks run in-frame so the experience stays inside our shell (betby-style).
      if (outcome.kind === 'iframe') {
        setEmbed({ url: outcome.url, method: outcome.method, payload: outcome.payload, title: game.name, game });
      } else if (outcome.kind === 'internal') {
        await navigate({ to: '/games/originals/$code', params: { code: outcome.code }, search: { session: outcome.session } });
      }
      // redirect/popup outcomes already navigated inside launchGame — nothing to embed.
    } catch (err) {
      if (!(err instanceof PopupBlockedError)) toast.error(mapApiError(err, t));
    } finally {
      setLaunchingId(null);
    }
  }

  if (embed) {
    return (
      <SeamlessGameFrame
        url={embed.url}
        method={embed.method}
        payload={embed.payload}
        title={embed.title}
        game={embed.game}
        onBack={() => setEmbed(null)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center gap-2.5">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--accent)]/15 text-[var(--accent)]">
          <TrendingUp size={22} />
        </span>
        <h1 className="bf-display text-[28px] md:text-[36px] leading-none text-[var(--text-primary)]">{t('tabs.sport')}</h1>
        {sportGames.length > 0 && (
          <span className="text-[13px] font-semibold text-[var(--text-muted)]">{sportGames.length}</span>
        )}
      </div>

      {gamesQuery.isPending ? (
        <GamesGridSkeleton count={8} />
      ) : gamesQuery.isError ? (
        <EmptyState
          icon="rotate"
          title={t('loadError.title')}
          body={t('loadError.body')}
          action={
            <Button size="sm" variant="secondary" onClick={() => void gamesQuery.refetch()}>
              {t('common:common.retry')}
            </Button>
          }
        />
      ) : sportGames.length === 0 ? (
        <div className="rounded-2xl bg-[var(--bg-elevated)] px-6 py-12 text-center text-[14px] text-[var(--text-muted)]">
          {t('sports.empty')}
        </div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
          {sportGames.map((g) => (
            <GameCard key={g.id} game={g as never} launching={launchingId === g.id} onPlay={() => void onPlay(g)} />
          ))}
        </div>
      )}

      <CasinoLiveBets />
      <SiteFooter />
    </div>
  );
}
